/**
 * @lineup-lite/hooks
 * https://github.com/sgratzl/lineup-lite
 *
 * Copyright (c) 2021 Samuel Gratzl <sam@sgratzl.com>
 */

import { categoricalStatsGenerator, numberStatsGenerator, dateStatsGenerator, textStatsGenerator, defaultCategoricalColorScale, resolveNumberFormatter, defaultScale, defaultConstantColorScale, NumberBar, clsx, DivergingNumberBar, BoxPlot, FilterRangeBoxPlot, Histogram, FilterBinHistogram, CategoricalColor, NumberColor, FilterRangeHistogram, resolveDateFormatter, DateLabel, NumberSymbol, TextSummary, FilterTextSummary, UpSetLine, UpSetPartialLine, FilterSetHistogram, HeatMap1D, BoxPlotArray, TextLabel, computeWeightedSumFactory, StackedBar, filterByCategoricalFilter, ALWAYS_TRUE, filterByRangeFilter, filterByCategoricalSetFilter, isCategoricalStats, isCategoricalSetValue, categoricalSetDegree, isNumberStats, isTextStats, isDateStats, defaultDivergingColorScale } from '@lineup-lite/components';
import React, { createContext, useCallback, useRef, useContext, forwardRef, useEffect } from 'react';
import { ensurePluginOrder, actions, defaultGroupByFn } from 'react-table';
import { startOfSecond, format, startOfMinute, startOfHour, getDayOfYear, startOfDay, getWeek, startOfWeek, startOfMonth, getQuarter, startOfQuarter } from 'date-fns';

function compute$2(gen, arr, preFilter) {
    if (!preFilter) {
        return gen(arr);
    }
    const stats = gen(arr);
    const lookup = new Map(stats.hist.map((bin) => [bin.x0, bin]));
    const hist = preFilter.hist.map((bin) => { var _a; return ({ ...bin, ...((_a = lookup.get(bin.x0)) !== null && _a !== void 0 ? _a : { count: 0 }) }); });
    return Object.assign(stats, {
        hist,
    });
}
function categoricalStats(options = {}) {
    const gen = categoricalStatsGenerator(options);
    return compute$2.bind(undefined, gen);
}

function compute$1(gen, options, arr, preFilter) {
    if (!preFilter) {
        return gen(arr);
    }
    const rawOptions = Object.assign({ min: preFilter.min, max: preFilter.max, numberOfBins: preFilter.hist.length }, options);
    return numberStatsGenerator(rawOptions)(arr);
}
function numberStats(options = {}) {
    const gen = numberStatsGenerator(options);
    return compute$1.bind(undefined, gen, options);
}
function computeArrayNumberStats(arr, column) {
    var _a, _b;
    if (arr == null) {
        return null;
    }
    if (arr._stats) {
        return arr._stats;
    }
    const stats = ((_a = column === null || column === void 0 ? void 0 : column.stats) !== null && _a !== void 0 ? _a : numberStatsGenerator())(arr, (_b = column === null || column === void 0 ? void 0 : column.preFilterStatsValue) !== null && _b !== void 0 ? _b : column === null || column === void 0 ? void 0 : column.statsValue);
    arr._stats = stats;
    return stats;
}

function compute(gen, options, arr, preFilter) {
    if (!preFilter) {
        return gen(arr);
    }
    const rawOptions = Object.assign({ min: preFilter.min, max: preFilter.max, histGranularity: preFilter.histGranularity }, options);
    return dateStatsGenerator(rawOptions)(arr);
}
function dateStats(options = {}) {
    const gen = dateStatsGenerator(options);
    return compute.bind(undefined, gen, options);
}

function textStats(options = {}) {
    return textStatsGenerator(options);
}

const EMPTY_ARR = [];
const optionContext = createContext({});
const statsGeneratorContext = createContext(null);
function isValueArray(props) {
    return Array.isArray(props.value);
}
function deriveStats(props, statsGen) {
    var _a;
    if (props.column.statsValue) {
        return {
            s: props.column.statsValue,
            preFilter: props.column.preFilterStatsValue,
        };
    }
    const flat = (_a = props.nonGroupedFlatRows) !== null && _a !== void 0 ? _a : props.flatRows;
    const values = flat.map((row) => row.values[props.column.id]);
    const { preFilteredRows } = props;
    const preFilterStats = preFilteredRows
        ? statsGen(preFilteredRows.map((row) => row.values[props.column.id]))
        : undefined;
    return {
        s: statsGen(values, preFilterStats),
        preFilter: preFilterStats,
    };
}
function extractStats(props, statsGen, expectArray = false) {
    const { s, preFilter } = deriveStats(props, statsGen);
    const cellProps = props;
    const cell = cellProps.cell;
    if (cell) {
        return {
            s: isValueArray(props) && !expectArray ? statsGen(props.value, s) : cellProps.value,
            preFilter,
            cell,
            base: s,
        };
    }
    return {
        s,
        preFilter,
        cell,
        isAggregated: false,
        base: null,
    };
}
function groupMaxBin(options, cell, props) {
    var _a, _b;
    if (options.maxBin != null || !cell.isAggregated) {
        return (_a = options.maxBin) !== null && _a !== void 0 ? _a : 0;
    }
    const groups = (_b = props.onlyGroupedFlatRows) !== null && _b !== void 0 ? _b : [];
    const stats = groups.map((group) => group.values[props.column.id]);
    return stats.reduce((acc, v) => (v != null && v.maxBin != null ? Math.max(acc, v.maxBin.count) : acc), 1);
}
function resolve(directValue, globalValue, defaultValue) {
    if (directValue != null) {
        return directValue;
    }
    if (globalValue != null) {
        return globalValue;
    }
    return defaultValue();
}
function isFilterAble(props) {
    if (!props || props.column == null) {
        return false;
    }
    return typeof props.column.setFilter === 'function';
}
function identity(v) {
    return v;
}
function generateIdentity() {
    return identity;
}
function generateColor() {
    return defaultCategoricalColorScale();
}
function useAsyncDebounce(f, timeout = 0) {
    const debounceRef = useRef(-1);
    return useCallback((arg) => {
        if (debounceRef.current >= 0) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            debounceRef.current = -1;
            f(arg);
        }, timeout);
    }, [f, timeout]);
}
function isMissing(v) {
    return (v == null ||
        (typeof v === 'number' && Number.isNaN(v)) ||
        (typeof v === 'string' && ['NaN', 'N/A', 'NA'].includes(v)));
}
function missingClass(v) {
    return isMissing(v) ? 'lt-missing' : undefined;
}

function deriveNumberOptions(props, options = {}) {
    const col = props.column;
    const stats = col.statsValue;
    return {
        scale: resolve(options.scale, stats === null || stats === void 0 ? void 0 : stats.scale, () => defaultScale),
        color: resolve(options.color, stats === null || stats === void 0 ? void 0 : stats.color, () => defaultConstantColorScale),
        format: resolveNumberFormatter(resolve(options.format, stats === null || stats === void 0 ? void 0 : stats.format, () => ({}))),
    };
}

function BarRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(NumberBar, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function BarRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(BarRenderer, Object.assign({}, props))));
}
function DivergingBarRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(DivergingNumberBar, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function DivergingBarRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(DivergingBarRenderer, Object.assign({}, props))));
}

function FilterBoxPlot(props) {
    const setFilter = useAsyncDebounce(props.setFilter);
    return React.createElement(FilterRangeBoxPlot, Object.assign({}, props, { setFilter: setFilter, summary: true }));
}
function BoxPlotRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : numberStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        return React.createElement(BoxPlot, { s: s, i18n: props.i18n, style: options.style, className: options.className });
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(FilterBoxPlot, { s: s, setFilter: setFilter, preFilter: preFilter, filterValue: filterValue, i18n: props.i18n, style: options.style, className: options.className }));
    }
    return (React.createElement(BoxPlot, { s: s, preFilter: preFilter, summary: true, i18n: props.i18n, style: options.style, className: options.className }));
}
function BoxPlotRendererFactory(options = {}) {
    const stats = numberStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(BoxPlotRenderer, Object.assign({}, props)))));
}

function CategoricalHistogramRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : categoricalStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        const maxBin = groupMaxBin(options, cell, props);
        return React.createElement(Histogram, { s: s, maxBin: maxBin, style: options.style, className: options.className });
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(FilterBinHistogram, { s: s, preFilter: preFilter, maxBin: options.maxBin, setFilter: setFilter, filterValue: filterValue, label: true, i18n: props.i18n, style: options.style, className: options.className }));
    }
    return React.createElement(Histogram, { s: s, maxBin: options.maxBin, label: true, summary: true, style: options.style, className: options.className });
}
function CategoricalHistogramRendererFactory(options = {}) {
    const stats = categoricalStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(CategoricalHistogramRenderer, Object.assign({}, props)))));
}

function deriveCategoricalOptions(props, options = {}) {
    const col = props.column;
    const stats = col.statsValue;
    return {
        format: resolve(options.format, stats === null || stats === void 0 ? void 0 : stats.format, generateIdentity),
        color: resolve(options.color, stats === null || stats === void 0 ? void 0 : stats.color, generateColor),
    };
}
function CategoricalRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveCategoricalOptions(props, options);
    return (React.createElement(CategoricalColor, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function CategoricalRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(CategoricalRenderer, Object.assign({}, props))));
}

function ColorRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(NumberColor, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function ColorRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(ColorRenderer, Object.assign({}, props))));
}

function Filtered$2(props) {
    const setFilter = useAsyncDebounce(props.setFilter);
    return React.createElement(FilterRangeHistogram, Object.assign({}, props, { setFilter: setFilter, i18n: props.i18n }));
}
function DateHistogramRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : dateStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        const maxBin = groupMaxBin(options, cell, props);
        return React.createElement(Histogram, { s: s, maxBin: maxBin });
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(Filtered$2, { s: s, preFilter: preFilter, maxBin: options.maxBin, setFilter: setFilter, filterValue: filterValue, i18n: props.i18n, style: options.style, className: options.className }));
    }
    return React.createElement(Histogram, { s: s, maxBin: options.maxBin, summary: true, style: options.style, className: options.className });
}
function DateHistogramRendererFactory(options = {}) {
    const stats = dateStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(DateHistogramRenderer, Object.assign({}, props)))));
}

function deriveDateOptions(props, options = {}) {
    const col = props.column;
    const stats = col.statsValue;
    return {
        format: resolveDateFormatter(resolve(options.format, stats === null || stats === void 0 ? void 0 : stats.format, () => ({}))),
    };
}
function DateRenderer(props) {
    const options = useContext(optionContext);
    if (typeof props.value === 'string') {
        return (React.createElement("div", { className: clsx('lt-date', options.className), style: options.style }, props.value));
    }
    const p = deriveDateOptions(props, options);
    return (React.createElement(DateLabel, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function DateRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(DateRenderer, Object.assign({}, props))));
}

function Filtered$1(props) {
    const setFilter = useAsyncDebounce(props.setFilter, 100);
    return React.createElement(FilterRangeHistogram, Object.assign({}, props, { setFilter: setFilter }));
}
function NumberHistogramRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : numberStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        const maxBin = groupMaxBin(options, cell, props);
        return React.createElement(Histogram, { s: s, maxBin: maxBin, style: options.style, className: options.className });
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(Filtered$1, { s: s, preFilter: preFilter, maxBin: options.maxBin, setFilter: setFilter, filterValue: filterValue, i18n: props.i18n, style: options.style, className: options.className }));
    }
    return React.createElement(Histogram, { s: s, maxBin: options.maxBin, summary: true, style: options.style, className: options.className });
}
function NumberHistogramRendererFactory(options = {}) {
    const stats = numberStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(NumberHistogramRenderer, Object.assign({}, props)))));
}

function ProportionalSymbolRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(NumberSymbol, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function ProportionalSymbolRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(ProportionalSymbolRenderer, Object.assign({}, props))));
}

function Filtered(props) {
    const setFilter = useAsyncDebounce(props.setFilter, 10);
    return React.createElement(FilterTextSummary, Object.assign({}, props, { setFilter: setFilter, i18n: props.i18n }));
}
function TextSummaryRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : textStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        return (React.createElement(TextSummary, { s: s, preFilter: preFilter, i18n: props.i18n, style: options.style, className: options.className }));
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(Filtered, { s: s, preFilter: preFilter, setFilter: setFilter, filterValue: filterValue, i18n: props.i18n, style: options.style, className: options.className }));
    }
    return (React.createElement(TextSummary, { s: s, preFilter: preFilter, summary: true, i18n: props.i18n, style: options.style, className: options.className }));
}
function TextSummaryRendererFactory(options = {}) {
    const stats = textStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(TextSummaryRenderer, Object.assign({}, props)))));
}

function GroupValueRenderer(props) {
    var _a;
    const value = (_a = props.row.groupByVal) !== null && _a !== void 0 ? _a : props.value;
    return React.createElement("div", { className: "lt-group" }, String(value));
}

function deriveCategoricalSetOptions(props, options = {}) {
    var _a;
    const col = props.column;
    const stats = col.statsValue;
    return {
        format: resolve(options.format, stats === null || stats === void 0 ? void 0 : stats.format, generateIdentity),
        color: resolve(options.color, stats === null || stats === void 0 ? void 0 : stats.color, generateColor),
        categories: (_a = stats === null || stats === void 0 ? void 0 : stats.categories) !== null && _a !== void 0 ? _a : EMPTY_ARR,
    };
}
function CategoricalSetRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveCategoricalSetOptions(props, options);
    return React.createElement(UpSetLine, Object.assign({}, p, { value: props.value, sets: p.categories, className: missingClass(props.value) }));
}
function CategoricalSetRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(CategoricalSetRenderer, Object.assign({}, props))));
}

function CategoricalSetHistogramRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : categoricalStats(options);
    const { s, preFilter, cell } = extractStats(props, stats);
    if (cell) {
        const groupValue = s.hist.map((bin) => (bin.count === 0 ? false : bin.count === s.count ? true : null));
        return React.createElement(UpSetPartialLine, Object.assign({}, s, { sets: s.categories, value: groupValue, i18n: props.i18n }));
    }
    if (isFilterAble(props) && props.column.canFilter) {
        const { setFilter, filterValue } = props.column;
        return (React.createElement(FilterSetHistogram, { s: s, preFilter: preFilter, maxBin: options.maxBin, setFilter: setFilter, filterValue: filterValue, label: true, i18n: props.i18n }));
    }
    return React.createElement(Histogram, { s: s, maxBin: options.maxBin, label: true, summary: true });
}
function CategoricalSetHistogramRendererFactory(options = {}) {
    const stats = categoricalStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(CategoricalSetHistogramRenderer, Object.assign({}, props)))));
}

function HeatMap1DRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(HeatMap1D, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function HeatMap1DRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(HeatMap1DRenderer, Object.assign({}, props))));
}

function BoxPlotArrayRenderer(props) {
    var _a;
    const options = useContext(optionContext);
    const stats = (_a = useContext(statsGeneratorContext)) !== null && _a !== void 0 ? _a : numberStats(options);
    const { s } = deriveStats(props, stats);
    const cellProps = props;
    return (React.createElement(BoxPlotArray, { value: cellProps.value, i18n: props.i18n, scale: s.scale, color: s.color, format: s.format, style: options.style, className: options.className }));
}
function BoxPlotArrayRendererFactory(options = {}) {
    const stats = numberStats(options);
    return (props) => (React.createElement(statsGeneratorContext.Provider, { value: stats },
        React.createElement(optionContext.Provider, { value: options },
            React.createElement(BoxPlotArrayRenderer, Object.assign({}, props)))));
}

function deriveTextOptions(props, options = {}) {
    const col = props.column;
    const stats = col.statsValue;
    return {
        format: resolve(options.format, stats === null || stats === void 0 ? void 0 : stats.format, () => (v) => v),
    };
}
function TextRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveTextOptions(props, options);
    return (React.createElement(TextLabel, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function TextRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(TextRenderer, Object.assign({}, props))));
}

function computeStackedValue(stack, colors = defaultCategoricalColorScale()) {
    const f = computeWeightedSumFactory(stack.map((s, i) => ({ color: colors(`${i}`), ...s })));
    const acc = stack.map((d) => (typeof d.col === 'function' ? d.col : (v) => v[d.col]));
    return (originalRow, index, sub) => {
        return f(acc.map((a) => a(originalRow, index, sub)));
    };
}
function StackedBarRenderer(props) {
    const options = useContext(optionContext);
    const p = deriveNumberOptions(props, options);
    return (React.createElement(StackedBar, Object.assign({}, p, { value: props.value, style: options.style, className: clsx(missingClass(props.value), options.className) })));
}
function StackedBarRendererFactory(options = {}) {
    return (props) => (React.createElement(optionContext.Provider, { value: options },
        React.createElement(StackedBarRenderer, Object.assign({}, props))));
}

function categoricalFilter(rows, ids, filterValue) {
    const f = filterByCategoricalFilter(filterValue);
    if (f === ALWAYS_TRUE) {
        return rows;
    }
    return rows.filter((row) => ids.some((id) => {
        const v = row.values[id];
        return f(v);
    }));
}
categoricalFilter.autoRemove = (val) => !Array.isArray(val);

function rangeFilter(rows, ids, filterValue) {
    const f = filterByRangeFilter(filterValue);
    if (f === ALWAYS_TRUE) {
        return rows;
    }
    return rows.filter((row) => ids.some((id) => {
        const v = row.values[id];
        return f(v);
    }));
}
rangeFilter.autoRemove = (val) => !Array.isArray(val);

function categoricalSetFilter(rows, ids, filterValue) {
    const f = filterByCategoricalSetFilter(filterValue);
    if (f === ALWAYS_TRUE) {
        return rows;
    }
    return rows.filter((row) => ids.some((id) => {
        const v = row.values[id];
        return f(v);
    }));
}
categoricalSetFilter.autoRemove = (val) => !Array.isArray(val);

function isStatsAggregateArray(v) {
    return Array.isArray(v) && typeof v._aggregate === 'function';
}
function statsAggregate(v) {
    if (Array.isArray(v)) {
        const copy = v.slice();
        copy._aggregate = (gen, stats) => gen(copy, stats);
        return copy;
    }
    return v;
}
function statsAggregateArray(v) {
    if (Array.isArray(v)) {
        const copy = v.slice();
        copy._aggregate = (gen, stats) => {
            const maxLength = copy.reduce((acc, vi) => Math.max(acc, Array.isArray(vi) ? vi.length : 0), 0);
            return Array(maxLength)
                .fill(0)
                .map((_, i) => gen(copy.map((d) => (Array.isArray(d) ? d[i] : null)), stats));
        };
        return copy;
    }
    return v;
}
function DummyComponent() {
    return null;
}
function useStats(hooks) {
    hooks.useInstance.push(useInstance$3);
}
useStats.pluginName = 'useStats';
function useInstance$3(instance) {
    ensurePluginOrder(instance.plugins, ['useFilters', 'useGroupBy'], 'useStats');
    const extendedInstance = instance;
    instance.allColumns.forEach((col) => {
        var _a, _b, _c;
        const extended = col;
        if (!extended.stats) {
            extended.statsValue = null;
            return;
        }
        const flat = (_a = extendedInstance.nonGroupedFlatRows) !== null && _a !== void 0 ? _a : instance.flatRows;
        const values = flat.map((row) => row.values[col.id]);
        const { preFilteredFlatRows } = extendedInstance;
        if (!extended.Summary) {
            extended.Summary = (_b = extended.Filter) !== null && _b !== void 0 ? _b : DummyComponent;
        }
        extended.preFilterStatsValue = preFilteredFlatRows
            ? extended.stats(preFilteredFlatRows.map((row) => row.values[col.id]))
            : undefined;
        extended.statsValue = extended.stats(values, extended.preFilterStatsValue);
        const grouped = (_c = extendedInstance.onlyGroupedFlatRows) !== null && _c !== void 0 ? _c : [];
        grouped.forEach((group) => {
            const value = group.values[col.id];
            if (isStatsAggregateArray(value)) {
                group.values[col.id] = value._aggregate(extended.stats, extended.statsValue);
            }
        });
    });
}

const IndeterminateCheckbox = forwardRef(function IndeterminateCheckbox({ indeterminate, ...rest }, ref) {
    const defaultRef = useRef(null);
    const resolvedRef = ref || defaultRef;
    useEffect(() => {
        const actRef = resolvedRef.current;
        if (actRef) {
            actRef.indeterminate = indeterminate !== null && indeterminate !== void 0 ? indeterminate : false;
        }
    }, [resolvedRef, indeterminate]);
    return React.createElement("input", Object.assign({ type: "checkbox", ref: resolvedRef }, rest));
});

function useRowSelectColumn(hooks) {
    hooks.visibleColumns.push(generateColumn$2);
}
useRowSelectColumn.pluginName = 'useRowSelectColumn';
const USE_ROW_SELECT_COLUMN_I18N_EN = {
    selectGroup: 'Click to select all rows in this group',
    unselectGroup: 'Click to unselect all rows in this group',
    selectAll: 'Click to select all rows',
    unselectAll: 'Click to unselect all rows',
    selectRow: 'Click to select this row',
    unselectRow: 'Click to unselect this row',
};
function Cell$2(props) {
    var _a, _b;
    const typedRow = props.row;
    const rows = props.rows;
    const i18n = {
        ...USE_ROW_SELECT_COLUMN_I18N_EN,
        ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
    };
    const p = { c: (_b = props.selectCheckboxComponent) !== null && _b !== void 0 ? _b : IndeterminateCheckbox };
    const onClick = useCallback((e) => {
        if (!e.shiftKey) {
            return;
        }
        const rowIndex = rows.indexOf(typedRow);
        if (rowIndex <= 0) {
            return;
        }
        const shouldSelect = e.currentTarget.checked;
        let rangeStart = rowIndex;
        while (rangeStart > 0 && rows[rangeStart - 1].isSelected !== shouldSelect) {
            rangeStart -= 1;
        }
        for (let i = rangeStart; i < rowIndex; i += 1) {
            rows[i].toggleRowSelected(shouldSelect);
        }
    }, [typedRow, rows]);
    return (React.createElement("div", { className: "lt-selection" },
        React.createElement(p.c, Object.assign({}, props.row.getToggleRowSelectedProps(), { onClick: onClick, title: props.row.isSelected ? i18n.unselectRow : i18n.selectRow }))));
}
function Summary$2() {
    return React.createElement("div", { className: "lt-summary " });
}
function Header$2(props) {
    var _a, _b;
    const i18n = {
        ...USE_ROW_SELECT_COLUMN_I18N_EN,
        ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
    };
    const p = { c: (_b = props.selectCheckboxComponent) !== null && _b !== void 0 ? _b : IndeterminateCheckbox };
    return (React.createElement("div", { className: "lt-selection" },
        React.createElement(p.c, Object.assign({}, props.getToggleAllRowsSelectedProps(), { title: props.isAllRowsSelected ? i18n.unselectAll : i18n.selectAll }))));
}
function generateColumn$2(columns, meta) {
    var _a;
    const width = (_a = meta.instance.selectColumnWidth) !== null && _a !== void 0 ? _a : 20;
    const selectionColumn = {
        id: 'selection',
        Header: Header$2,
        Summary: Summary$2,
        Cell: Cell$2,
        minWidth: width,
        width,
        maxWidth: width,
        disableFilters: true,
        disableSortBy: true,
        disableGroupBy: true,
        disableResizing: true,
        canHide: false,
        isSupport: true,
    };
    return [selectionColumn, ...columns];
}

const USE_EXPAND_COLUMN_I18N_EN = {
    expandGroup: 'Click to expand this group',
    collapseGroup: 'Click to collapse this group',
    expandAllGroups: 'Click to expand all groups',
    collapseAllGroups: 'Click to collapse all groups',
};
function useRowExpandColumn(hooks) {
    hooks.visibleColumns.push(generateColumn$1);
}
useRowExpandColumn.pluginName = 'useRowExpandColumn';
function ArrowDropRightLine() {
    return React.createElement(React.Fragment, null, "\u25B8");
}
function Cell$1() {
    return null;
}
function Aggregated$1(props) {
    var _a, _b;
    const row = props.row;
    if (!row.canExpand) {
        return null;
    }
    const i18n = {
        ...USE_EXPAND_COLUMN_I18N_EN,
        ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
    };
    return (React.createElement("button", Object.assign({}, row.getToggleRowExpandedProps({
        className: clsx('lt-expand-agg', `lt-expand-${row.depth}`, row.isExpanded && 'lt-expanded'),
    }), { type: "button", title: row.isExpanded ? i18n.collapseGroup : i18n.expandGroup }), ((_b = props.icons) === null || _b === void 0 ? void 0 : _b.expandGroup) ? React.createElement(props.icons.expandGroup, null) : React.createElement(ArrowDropRightLine, null)));
}
function Summary$1(props) {
    var _a, _b;
    if (!props.allColumns.some((d) => d.isGrouped)) {
        return React.createElement("div", { className: "lt-summary" });
    }
    const i18n = {
        ...USE_EXPAND_COLUMN_I18N_EN,
        ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
    };
    return (React.createElement("div", { className: "lt-summary" },
        React.createElement("button", Object.assign({}, props.getToggleAllRowsExpandedProps({
            className: clsx('lt-expand-agg', props.isAllRowsExpanded && 'lt-expanded'),
        }), { type: "button", title: props.isAllRowsExpanded ? i18n.collapseAllGroups : i18n.expandAllGroups }), ((_b = props.icons) === null || _b === void 0 ? void 0 : _b.expandGroup) ? React.createElement(props.icons.expandGroup, null) : React.createElement(ArrowDropRightLine, null))));
}
function Header$1() {
    return React.createElement(React.Fragment, null);
}
function generateColumn$1(columns, meta) {
    var _a;
    const width = (_a = meta.instance.expandColumnWidth) !== null && _a !== void 0 ? _a : 20;
    const expandColumn = {
        id: 'expand',
        Header: Header$1,
        Summary: Summary$1,
        Aggregated: Aggregated$1,
        Cell: Cell$1,
        minWidth: width,
        width,
        maxWidth: width,
        disableFilters: true,
        disableSortBy: true,
        disableGroupBy: true,
        disableResizing: true,
        canHide: false,
        isSupport: true,
    };
    return [expandColumn, ...columns];
}

function useRowRankColumn(hooks) {
    hooks.visibleColumns.push(generateColumn);
    hooks.useInstance.push(useInstance$2);
}
useRowRankColumn.pluginName = 'useRowRankColumn';
function Cell(props) {
    return React.createElement("div", { className: "lt-rank" }, `${props.row.rank.toLocaleString()}.`);
}
function Aggregated(props) {
    const group = props.row.subRows;
    if (group.length === 0) {
        return React.createElement("div", { className: "lt-rank-agg" });
    }
    const firstRow = group[0];
    const lastRow = group[group.length - 1];
    return (React.createElement("div", { className: "lt-rank-agg" },
        React.createElement("span", null, `${firstRow.rank.toLocaleString()}.`),
        React.createElement("span", { className: "lt-rank-agg-spacer" }),
        React.createElement("span", null, `${lastRow.rank.toLocaleString()}.`)));
}
function Summary() {
    return React.createElement("div", { className: "lt-summary" });
}
function Header() {
    return React.createElement("div", null, "#");
}
function generateColumn(columns, meta) {
    var _a;
    const width = (_a = meta.instance.rankColumnWidth) !== null && _a !== void 0 ? _a : 40;
    const rankColumn = {
        id: 'rank',
        Header,
        Summary,
        Aggregated,
        Cell,
        minWidth: width - 10,
        width,
        maxWidth: width + 10,
        disableFilters: true,
        disableSortBy: true,
        disableGroupBy: true,
        canHide: false,
        isSupport: true,
    };
    return [rankColumn, ...columns];
}
function useInstance$2(instance) {
    ensurePluginOrder(instance.plugins, ['useFilters', 'useGroupBy', 'useSortBy'], 'useRowRankColumn');
    const extendedInstance = instance;
    let rank = 1;
    let groupRank = 1;
    extendedInstance.flatRows.forEach((row) => {
        if (row.isGrouped) {
            row.rank = groupRank;
            groupRank += 1;
            return;
        }
        row.rank = rank;
        rank += 1;
    });
}

function useGroupingOptions(hooks) {
    hooks.stateReducers.push(reducer$1);
    hooks.useInstance.push(useInstance$1);
}
useGroupingOptions.pluginName = 'useGroupingOptions';
actions.setGroupingOptions = 'setGroupingOptions';
function reducer$1(state, action, _previousState, instance) {
    var _a, _b, _c, _d, _e, _f;
    const { allColumns } = instance;
    if (action.type === actions.init) {
        return {
            groupingOptions: [],
            ...state,
        };
    }
    if (action.type === actions.setGroupingOptions) {
        const { columnId, groupingOptions } = action;
        const column = allColumns.find((d) => d.id === columnId);
        if (!column) {
            throw new Error(`React-Table: Could not find a column with id: ${columnId}`);
        }
        const previousFilter = (_a = state.groupingOptions) === null || _a === void 0 ? void 0 : _a.find((d) => d.id === columnId);
        if (groupingOptions == null) {
            return {
                ...state,
                groupingOptions: (_c = (_b = state.groupingOptions) === null || _b === void 0 ? void 0 : _b.filter((d) => d.id !== columnId)) !== null && _c !== void 0 ? _c : [],
            };
        }
        if (previousFilter) {
            return {
                ...state,
                groupingOptions: (_e = (_d = state.groupingOptions) === null || _d === void 0 ? void 0 : _d.map((d) => {
                    if (d.id === columnId) {
                        return { id: columnId, value: groupingOptions };
                    }
                    return d;
                })) !== null && _e !== void 0 ? _e : [],
            };
        }
        return {
            ...state,
            groupingOptions: [...((_f = state.groupingOptions) !== null && _f !== void 0 ? _f : []), { id: columnId, value: groupingOptions }],
        };
    }
    return undefined;
}
function useInstance$1(instance) {
    const { dispatch, allColumns, state } = instance;
    const { groupingOptions = [] } = state;
    const setGroupingOptions = React.useCallback((columnId, value) => {
        dispatch({ type: actions.setGroupingOptions, columnId, groupingOptions: value });
    }, [dispatch]);
    allColumns.forEach((column) => {
        const cc = column;
        cc.setGroupingOptions = (val) => setGroupingOptions(column.id, val);
        const found = groupingOptions.find((d) => d.id === column.id);
        cc.groupingOptions = found && found.value;
    });
    Object.assign(instance, {
        setGroupingOptions,
    });
}

function useSortingOptions(hooks) {
    hooks.stateReducers.push(reducer);
    hooks.useInstance.push(useInstance);
}
useSortingOptions.pluginName = 'useSortingOptions';
actions.setSortingOptions = 'setSortingOptions';
function reducer(state, action, _previousState, instance) {
    var _a, _b, _c, _d, _e, _f;
    const { allColumns } = instance;
    if (action.type === actions.init) {
        return {
            sortingOptions: [],
            ...state,
        };
    }
    if (action.type === actions.setSortingOptions) {
        const { columnId, sortingOptions } = action;
        const column = allColumns.find((d) => d.id === columnId);
        if (!column) {
            throw new Error(`React-Table: Could not find a column with id: ${columnId}`);
        }
        const previousFilter = (_a = state.sortingOptions) === null || _a === void 0 ? void 0 : _a.find((d) => d.id === columnId);
        if (sortingOptions == null) {
            return {
                ...state,
                sortingOptions: (_c = (_b = state.sortingOptions) === null || _b === void 0 ? void 0 : _b.filter((d) => d.id !== columnId)) !== null && _c !== void 0 ? _c : [],
            };
        }
        if (previousFilter) {
            return {
                ...state,
                sortingOptions: (_e = (_d = state.sortingOptions) === null || _d === void 0 ? void 0 : _d.map((d) => {
                    if (d.id === columnId) {
                        return { id: columnId, value: sortingOptions };
                    }
                    return d;
                })) !== null && _e !== void 0 ? _e : [],
            };
        }
        return {
            ...state,
            sortingOptions: [...((_f = state.sortingOptions) !== null && _f !== void 0 ? _f : []), { id: columnId, value: sortingOptions }],
        };
    }
    return undefined;
}
function useInstance(instance) {
    const { dispatch, allColumns, state } = instance;
    const { sortingOptions = [] } = state;
    const setSortingOptions = React.useCallback((columnId, value) => {
        dispatch({ type: actions.setSortingOptions, columnId, sortingOptions: value });
    }, [dispatch]);
    allColumns.forEach((column) => {
        const cc = column;
        cc.setSortingOptions = (val) => setSortingOptions(column.id, val);
        const found = sortingOptions.find((d) => d.id === column.id);
        cc.sortingOptions = found && found.value;
    });
    Object.assign(instance, {
        setSortingOptions,
    });
}

function compareAsc(va, vb) {
    if (va === vb) {
        return 0;
    }
    if (va == null) {
        return 1;
    }
    if (vb == null) {
        return -1;
    }
    return va < vb ? -1 : 1;
}
function categoricalSortFunc(categories) {
    const lookup = new Map(categories.map((cat, i) => [cat, i]));
    return (va, vb) => {
        var _a, _b;
        const aIndex = (_a = lookup.get(va)) !== null && _a !== void 0 ? _a : Number.POSITIVE_INFINITY;
        const bIndex = (_b = lookup.get(vb)) !== null && _b !== void 0 ? _b : Number.POSITIVE_INFINITY;
        return compareAsc(aIndex, bIndex);
    };
}

function sortCompare(a, b, columnId) {
    const va = a.values[columnId];
    const vb = b.values[columnId];
    return compareAsc(va, vb);
}

function resolveGroupByColumn(a) {
    const g = a.isGrouped;
    if (!g) {
        return null;
    }
    return a.groupByID;
}
function resolveSortOptions(a, columnId) {
    var _a, _b;
    const column = (_b = (_a = a.allCells) === null || _a === void 0 ? void 0 : _a.find((d) => d.column.id === columnId)) === null || _b === void 0 ? void 0 : _b.column;
    if (!column) {
        return undefined;
    }
    return column.sortingOptions;
}
function sortSplitter(rows, group) {
    return (a, b, columnId) => {
        const aGroupByColumn = resolveGroupByColumn(a);
        const bGroupByColumn = resolveGroupByColumn(b);
        const options = resolveSortOptions(a, columnId);
        if ((!aGroupByColumn || aGroupByColumn === columnId) && (!bGroupByColumn || bGroupByColumn === columnId)) {
            return rows(a, b, columnId, options);
        }
        if (aGroupByColumn && bGroupByColumn) {
            return group(a, b, columnId, options);
        }
        return rows(a, b, columnId, options);
    };
}

function categoricalGroupCompare(a, b, columnId) {
    const av = a.values[columnId];
    const bv = b.values[columnId];
    if (isCategoricalStats(av) && isCategoricalStats(bv)) {
        const ai = av.categories.indexOf(av.maxBin.x0);
        const bi = av.categories.indexOf(bv.maxBin.x0);
        return compareAsc(ai, bi);
    }
    return compareAsc(av, bv);
}
function categoricalSort(categories) {
    const sorter = categoricalSortFunc(categories);
    return (a, b, columnId) => {
        const va = a.values[columnId];
        const vb = b.values[columnId];
        return sorter(va, vb);
    };
}

function toMode(s, bin) {
    if (bin < 0 || s.hist.length <= bin) {
        return 4;
    }
    const binValue = s.hist[bin].count;
    const maxCount = s.flatItems.length;
    if (maxCount === binValue) {
        return 1;
    }
    if (binValue > 0) {
        return 2;
    }
    return 3;
}
function categoricalSetGroupCompare(a, b, columnId) {
    const av = a.values[columnId];
    const bv = b.values[columnId];
    if (isCategoricalStats(av) && isCategoricalStats(bv)) {
        for (let i = 0; i < av.hist.length; i += 1) {
            const avi = toMode(av, i);
            const bvi = toMode(bv, i);
            if (avi !== bvi) {
                return compareAsc(avi, bvi);
            }
        }
        return 0;
    }
    return compareAsc(av, bv);
}
function categoricalSetIndices(value, lookup) {
    var _a;
    const vs = [];
    for (const vi of value) {
        vs.push(lookup ? (_a = lookup.get(vi)) !== null && _a !== void 0 ? _a : Number.POSITIVE_INFINITY : vi);
    }
    return vs.sort(compareAsc);
}
function categoricalSetCompare(categories) {
    const lookup = categories ? new Map(categories.map((cat, i) => [cat, i])) : undefined;
    return (a, b, columnId) => {
        const va = a.values[columnId];
        const vb = b.values[columnId];
        if (!isCategoricalSetValue(va) || !isCategoricalSetValue(vb)) {
            return compareAsc(va, vb);
        }
        const degreeA = categoricalSetDegree(va);
        const degreeB = categoricalSetDegree(vb);
        if (degreeA !== degreeB) {
            return degreeA - degreeB;
        }
        const iA = categoricalSetIndices(va, lookup);
        const iB = categoricalSetIndices(vb, lookup);
        const min = Math.min(iA.length, iB.length);
        for (let i = 0; i < min; i += 1) {
            if (iA[i] === iB[i]) {
                continue;
            }
            if (iA[i] < iB[i]) {
                return -1;
            }
            return 1;
        }
        return 0;
    };
}

function numberGroupCompare(a, b, columnId, options) {
    var _a;
    const group = (_a = options === null || options === void 0 ? void 0 : options.group) !== null && _a !== void 0 ? _a : 'median';
    const av = a.values[columnId];
    const bv = b.values[columnId];
    if (isNumberStats(av) && isNumberStats(bv)) {
        return compareAsc(av[group], bv[group]);
    }
    return compareAsc(av, bv);
}
function numbersCompare(a, b, columnId, options) {
    var _a, _b, _c;
    const group = (_a = options === null || options === void 0 ? void 0 : options.group) !== null && _a !== void 0 ? _a : 'median';
    const column = (_c = (_b = a.allCells) === null || _b === void 0 ? void 0 : _b.find((d) => d.column.id === columnId)) === null || _c === void 0 ? void 0 : _c.column;
    const av = computeArrayNumberStats(a.values[columnId], column);
    const bv = computeArrayNumberStats(b.values[columnId], column);
    if (isNumberStats(av) && isNumberStats(bv)) {
        return compareAsc(av[group], bv[group]);
    }
    return compareAsc(av, bv);
}
function numberGroupCompareFactory(options) {
    return (a, b, columId, o) => numberGroupCompare(a, b, columId, o !== null && o !== void 0 ? o : options);
}
function numbersCompareFactory(options) {
    return (a, b, columId, o) => numbersCompare(a, b, columId, o !== null && o !== void 0 ? o : options);
}

function textGroupCompare(a, b, columnId) {
    var _a, _b;
    const av = a.values[columnId];
    const bv = b.values[columnId];
    if (isTextStats(av) && isTextStats(bv)) {
        return compareAsc((_a = av.mostFrequent[0]) === null || _a === void 0 ? void 0 : _a.count, (_b = bv.mostFrequent[0]) === null || _b === void 0 ? void 0 : _b.count);
    }
    return compareAsc(av, bv);
}

function dateGroupCompare(a, b, columnId, options) {
    var _a;
    const group = (_a = options === null || options === void 0 ? void 0 : options.group) !== null && _a !== void 0 ? _a : 'min';
    const av = a.values[columnId];
    const bv = b.values[columnId];
    if (isDateStats(av) && isDateStats(bv)) {
        return compareAsc(av[group], bv[group]);
    }
    return compareAsc(av, bv);
}
function dateGroupCompareFactory(options) {
    return (a, b, columId, o) => dateGroupCompare(a, b, columId, o !== null && o !== void 0 ? o : options);
}

function hasGroupByFunction(cell) {
    return (cell != null &&
        cell.column != null &&
        typeof cell.column.groupBy === 'function');
}
function columnSpecificGroupByFn(rows, columnId) {
    var _a;
    if (rows.length === 0 || rows[0] == null || rows[0].allCells == null) {
        return {};
    }
    const cell = (_a = rows[0].allCells) === null || _a === void 0 ? void 0 : _a.find((d) => d.column.id === columnId);
    if (hasGroupByFunction(cell)) {
        return cell.column.groupBy(rows, cell.column, cell.column.groupingOptions);
    }
    return defaultGroupByFn(rows, columnId);
}

const MISSING_GROUP = 'Missing Values';
const OTHERS_GROUPS = 'Other Values';
function histGroupBy(rows, column) {
    const stats = column.statsValue;
    if (stats == null) {
        return defaultGroupByFn(rows, column.id);
    }
    const r = {};
    stats.hist.forEach((bin, i) => {
        const key = bin.label;
        const group = rows.filter((row) => {
            let v = row.values[column.id];
            if (v == null) {
                return false;
            }
            if (Array.isArray(v)) {
                const arrayStats = computeArrayNumberStats(v, column);
                v = arrayStats.median;
            }
            return (v != null &&
                ((bin.x0 <= v && v < bin.x1) || (v < bin.x1 && i === 0) || (i === stats.hist.length - 1 && v >= bin.x0)));
        });
        if (group.length > 0) {
            r[key] = group;
        }
    });
    const group = rows.filter((row) => row.values[column.id] == null);
    if (group.length > 0) {
        r[MISSING_GROUP] = group;
    }
    return r;
}

function toSpecialIndex(v) {
    if (v === MISSING_GROUP) {
        return 2;
    }
    if (v === OTHERS_GROUPS) {
        return 1;
    }
    return 0;
}
function compareMissing(a, b) {
    const aIndex = toSpecialIndex(a);
    const bIndex = toSpecialIndex(b);
    if (aIndex !== bIndex) {
        return aIndex - bIndex;
    }
    return compareAsc(a, b);
}
function sortGroups(r) {
    const sorted = {};
    for (const key of Object.keys(r).sort(compareMissing)) {
        sorted[key] = r[key];
    }
    return sorted;
}
function baseGroupBy(rows, column, acc = (v) => (v ? String(v) : null)) {
    var _a;
    const r = {};
    for (const row of rows) {
        const value = (_a = acc(row.values[column.id])) !== null && _a !== void 0 ? _a : MISSING_GROUP;
        const rValue = r[value];
        if (!rValue) {
            r[value] = [row];
        }
        else {
            rValue.push(row);
        }
    }
    return sortGroups(r);
}

function categoricalGroupBy(rows, column) {
    if (rows.length === 0) {
        return {};
    }
    const base = baseGroupBy(rows, column);
    if (column.statsValue == null) {
        return base;
    }
    const stats = column.statsValue;
    const sortedGrouped = {};
    stats.categories.forEach((cat) => {
        if (base[cat] != null) {
            sortedGrouped[cat] = base[cat];
            delete base[cat];
        }
    });
    return Object.assign(sortedGrouped, base);
}

function padLeft(v, size = 2) {
    const s = String(v);
    if (s.length >= size) {
        return s;
    }
    if (typeof s.padStart === 'function') {
        return s.padStart(size, '0');
    }
    const rest = size - s.length;
    if (rest === 1) {
        return `0${s}`;
    }
    if (rest === 2) {
        return `00${s}`;
    }
    return `${Array(rest).fill('0').join()}${s}`;
}
function toGroup(value, grouper) {
    if (value == null) {
        return { name: MISSING_GROUP, value: Number.NaN };
    }
    switch (grouper.granularity) {
        case 'century': {
            const centuryP = Math.floor(value.getFullYear() / 100);
            if (grouper.circular) {
                const century = centuryP % 10;
                return {
                    value: century,
                    name: `*${century}00`,
                };
            }
            return {
                value: centuryP,
                name: `${centuryP}00`,
            };
        }
        case 'decade': {
            const decadeP = Math.floor(value.getFullYear() / 10);
            if (grouper.circular) {
                const decade = decadeP % 10;
                return {
                    value: decade,
                    name: `**${decade}0`,
                };
            }
            return {
                value: decadeP,
                name: `${decadeP}0`,
            };
        }
        case 'year':
            if (grouper.circular) {
                const year = value.getFullYear() % 10;
                return {
                    value: year,
                    name: `***${year}`,
                };
            }
            return {
                value: value.getFullYear(),
                name: format(value, 'yyyy'),
            };
        case 'quarter':
            if (grouper.circular) {
                return {
                    value: getQuarter(value),
                    name: format(value, 'qqq'),
                };
            }
            return {
                value: startOfQuarter(value).getTime(),
                name: format(value, 'yyyy qqq'),
            };
        case 'month':
            if (grouper.circular) {
                return {
                    value: value.getMonth(),
                    name: format(value, 'LLLL'),
                };
            }
            return {
                value: startOfMonth(value).getTime(),
                name: format(value, 'yyyy LLLL'),
            };
        case 'week':
            if (grouper.circular) {
                return {
                    value: getWeek(value),
                    name: format(value, 'ww'),
                };
            }
            return {
                value: startOfWeek(value).getTime(),
                name: format(value, 'yyyy ww'),
            };
        case 'day_of_week': {
            if (grouper.circular) {
                return {
                    value: value.getDay(),
                    name: format(value, 'cccc'),
                };
            }
            const s = startOfDay(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPP'),
            };
        }
        case 'day_of_month': {
            if (grouper.circular) {
                return {
                    value: value.getDate(),
                    name: padLeft(value.getDate()),
                };
            }
            const s = startOfDay(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPP'),
            };
        }
        case 'day_of_year': {
            if (grouper.circular) {
                const v = getDayOfYear(value);
                return {
                    value: v,
                    name: format(value, 'DDD'),
                };
            }
            const s = startOfDay(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPP'),
            };
        }
        case 'hour': {
            if (grouper.circular) {
                return {
                    value: value.getHours(),
                    name: padLeft(value.getHours(), 2),
                };
            }
            const s = startOfHour(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPpp'),
            };
        }
        case 'minute': {
            if (grouper.circular) {
                return {
                    value: value.getMinutes(),
                    name: padLeft(value.getMinutes()),
                };
            }
            const s = startOfMinute(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPpp'),
            };
        }
        default: {
            if (grouper.circular) {
                return {
                    value: value.getSeconds(),
                    name: padLeft(value.getSeconds()),
                };
            }
            const s = startOfSecond(value);
            return {
                value: s.getTime(),
                name: format(s, 'PPpp'),
            };
        }
    }
}
function dateGroupBy(rows, column, options) {
    var _a;
    if (!options) {
        return histGroupBy(rows, column);
    }
    const map = new Map();
    for (const row of rows) {
        const v = (_a = row.values[column.id]) !== null && _a !== void 0 ? _a : null;
        const group = toGroup(v, options);
        const entry = map.get(group.name);
        if (entry) {
            entry.rows.push(row);
        }
        else {
            map.set(group.name, { rows: [row], ...group });
        }
    }
    const r = {};
    Array.from(map.values())
        .sort((a, b) => {
        if (a.name === MISSING_GROUP) {
            return b.name === MISSING_GROUP ? 0 : 1;
        }
        if (b.name === MISSING_GROUP) {
            return -1;
        }
        return a.value - b.value;
    })
        .forEach((entry) => {
        r[entry.name] = entry.rows;
    });
    return r;
}
function dateGroupByFactory(options) {
    return (rows, column, o) => dateGroupBy(rows, column, o !== null && o !== void 0 ? o : options);
}

function numberGroupBy(rows, column, options) {
    var _a, _b, _c;
    if (!options) {
        return histGroupBy(rows, column);
    }
    const stats = column.statsValue;
    let { thresholds } = options;
    if (!thresholds || thresholds.length === 0) {
        if (stats) {
            thresholds = [(stats.max - stats.min) / 2];
        }
        else {
            thresholds = [0.5];
        }
    }
    if (thresholds.length === 0) {
        return histGroupBy(rows, column);
    }
    const groups = thresholds.map((v, i) => {
        var _a, _b, _c;
        if (i === 0) {
            return {
                label: `<= ${(_a = stats === null || stats === void 0 ? void 0 : stats.format(v)) !== null && _a !== void 0 ? _a : v.toLocaleString()}`,
                rows: [],
                value: v,
            };
        }
        const prev = thresholds[i - 1];
        return {
            label: `${(_b = stats === null || stats === void 0 ? void 0 : stats.format(prev)) !== null && _b !== void 0 ? _b : prev.toLocaleString()} ... ${(_c = stats === null || stats === void 0 ? void 0 : stats.format(v)) !== null && _c !== void 0 ? _c : v.toLocaleString()}`,
            rows: [],
            value: v,
        };
    });
    const last = thresholds[thresholds.length - 1];
    const largest = {
        label: `>= ${(_a = stats === null || stats === void 0 ? void 0 : stats.format(last)) !== null && _a !== void 0 ? _a : last.toLocaleString()}`,
        rows: [],
        value: last,
    };
    const r = {};
    for (const v of groups) {
        r[v.value] = [];
    }
    r[largest.label] = largest.rows;
    r[MISSING_GROUP] = [];
    for (const row of rows) {
        const v = (_b = row.values[column.id]) !== null && _b !== void 0 ? _b : null;
        if (v == null) {
            r[MISSING_GROUP].push(row);
            continue;
        }
        const group = (_c = groups.find((g) => v < g.value)) !== null && _c !== void 0 ? _c : largest;
        group.rows.push(row);
    }
    for (const key of Object.keys(r)) {
        if (r[key].length === 0) {
            delete r[key];
        }
    }
    return r;
}
function numberGroupByFactory(options) {
    return (rows, column, o) => numberGroupBy(rows, column, o !== null && o !== void 0 ? o : options);
}

function textGroupBy(rows, column, options) {
    var _a;
    if (!options) {
        return baseGroupBy(rows, column);
    }
    const r = {};
    let toGroup = (v) => v || MISSING_GROUP;
    switch (options.type) {
        case 'regex':
            toGroup = (v) => { var _a, _b; return (_b = (_a = options.values.find((d) => v != null && d.test(v))) === null || _a === void 0 ? void 0 : _a.source) !== null && _b !== void 0 ? _b : OTHERS_GROUPS; };
            break;
        case 'value':
            toGroup = (v) => { var _a; return (_a = options.values.find((d) => v != null && d === v)) !== null && _a !== void 0 ? _a : OTHERS_GROUPS; };
            break;
        case 'startsWith':
            toGroup = (v) => { var _a; return (_a = options.values.find((d) => v != null && v.startsWith(d))) !== null && _a !== void 0 ? _a : OTHERS_GROUPS; };
            break;
    }
    for (const row of rows) {
        const v = (_a = row.values[column.id]) !== null && _a !== void 0 ? _a : null;
        const g = v != null ? toGroup(v) : MISSING_GROUP;
        const rValue = r[g];
        if (!rValue) {
            r[g] = [row];
        }
        else {
            rValue.push(row);
        }
    }
    return sortGroups(r);
}
function textGroupByFactory(options) {
    return (rows, column, o) => textGroupBy(rows, column, o !== null && o !== void 0 ? o : options);
}

function categoricalSetGroupBy(rows, column) {
    if (rows.length === 0) {
        return {};
    }
    const stats = column.statsValue;
    const sorter = stats ? categoricalSortFunc(stats.categories) : compareAsc;
    return baseGroupBy(rows, column, (d) => (d == null ? null : [...d].sort(sorter).join(',')));
}

function guessName(acc) {
    return acc
        .replace(/[-_ ]+/gm, ' ')
        .split(/(?=[A-Z ])/)
        .map((d) => (d.length <= 1 ? d : `${d[0].toUpperCase()}${d.slice(1)}`))
        .join(' ');
}
function asColumn(col) {
    if (typeof col === 'string') {
        return {
            Header: guessName(col),
            accessor: col,
        };
    }
    return col;
}
function asTextColumn(col, options) {
    return {
        Cell: TextRenderer,
        Summary: TextSummaryRenderer,
        Aggregated: TextSummaryRenderer,
        Group: GroupValueRenderer,
        aggregate: statsAggregate,
        filter: 'text',
        stats: textStats(options),
        sortType: sortSplitter(sortCompare, textGroupCompare),
        defaultCanSort: true,
        groupBy: textGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asNumberColumn(col, options) {
    return {
        Cell: BarRenderer,
        Summary: NumberHistogramRenderer,
        Group: GroupValueRenderer,
        Aggregated: NumberHistogramRenderer,
        aggregate: statsAggregate,
        filter: rangeFilter,
        stats: numberStats(options),
        sortType: sortSplitter(sortCompare, numberGroupCompare),
        sortDescFirst: true,
        defaultCanGroupBy: false,
        groupBy: numberGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asDivergingNumberColumn(col, options = {}) {
    return {
        Cell: DivergingBarRendererFactory({ center: options.center }),
        Summary: NumberHistogramRenderer,
        Group: GroupValueRenderer,
        Aggregated: NumberHistogramRenderer,
        aggregate: statsAggregate,
        filter: rangeFilter,
        stats: numberStats({ color: defaultDivergingColorScale, center: 0, ...options }),
        sortType: sortSplitter(sortCompare, numberGroupCompare),
        sortDescFirst: true,
        defaultCanGroupBy: false,
        groupBy: numberGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asNumbersColumn(col, options) {
    return {
        Cell: HeatMap1DRenderer,
        Summary: NumberHistogramRenderer,
        Group: GroupValueRenderer,
        Aggregated: BoxPlotArrayRenderer,
        aggregate: statsAggregateArray,
        filter: rangeFilter,
        stats: numberStats(options),
        sortType: sortSplitter(numbersCompare, numberGroupCompare),
        sortDescFirst: true,
        defaultCanGroupBy: false,
        groupBy: numberGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asNumberBoxPlotColumn(col, options) {
    return {
        Cell: BarRenderer,
        Summary: BoxPlotRenderer,
        Group: GroupValueRenderer,
        Aggregated: BoxPlotRenderer,
        aggregate: statsAggregate,
        filter: rangeFilter,
        stats: numberStats(options),
        sortType: sortSplitter(sortCompare, numberGroupCompare),
        sortDescFirst: true,
        defaultCanGroupBy: false,
        groupBy: numberGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asCategoricalColumn(col, options) {
    return {
        Cell: CategoricalRenderer,
        Summary: CategoricalHistogramRenderer,
        Group: CategoricalRenderer,
        Aggregated: CategoricalHistogramRenderer,
        aggregate: statsAggregate,
        filter: categoricalFilter,
        stats: categoricalStats(options),
        sortType: sortSplitter(options && options.categories ? categoricalSort(options.categories) : sortCompare, categoricalGroupCompare),
        defaultCanSort: true,
        groupBy: categoricalGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asCategoricalSetColumn(col, options) {
    return {
        Cell: CategoricalSetRenderer,
        Summary: CategoricalSetHistogramRenderer,
        Group: CategoricalSetRenderer,
        Aggregated: CategoricalSetHistogramRenderer,
        aggregate: statsAggregate,
        filter: categoricalSetFilter,
        stats: categoricalStats(options),
        sortType: sortSplitter(options && options.categories ? categoricalSetCompare(options.categories) : categoricalSetCompare(), categoricalSetGroupCompare),
        defaultCanSort: true,
        groupBy: categoricalSetGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asDateColumn(col, options) {
    return {
        Cell: DateRenderer,
        Summary: DateHistogramRenderer,
        Group: GroupValueRenderer,
        Aggregated: DateHistogramRenderer,
        aggregate: statsAggregate,
        filter: rangeFilter,
        stats: dateStats(options),
        sortType: sortSplitter(sortCompare, dateGroupCompare),
        sortDescFirst: true,
        groupBy: dateGroupBy,
        canHide: false,
        ...asColumn(col),
    };
}
function asStackedNumberColumn(col, stack, options) {
    var _a;
    return {
        Cell: StackedBarRenderer,
        Summary: NumberHistogramRenderer,
        Group: GroupValueRenderer,
        Aggregated: NumberHistogramRenderer,
        aggregate: statsAggregate,
        filter: rangeFilter,
        stats: numberStats({ color: defaultConstantColorScale, ...(options !== null && options !== void 0 ? options : {}) }),
        sortType: sortSplitter(sortCompare, numberGroupCompare),
        sortDescFirst: true,
        defaultCanGroupBy: false,
        groupBy: numberGroupBy,
        canHide: false,
        id: typeof col == 'string' ? col : (_a = col.id) !== null && _a !== void 0 ? _a : col.Header,
        ...(typeof col === 'string' ? { Header: col } : col),
        accessor: computeStackedValue(stack, options === null || options === void 0 ? void 0 : options.colors),
    };
}

function cleanCategories(categories) {
    categories.delete(null);
    categories.delete(undefined);
    categories.delete('');
    categories.delete('NA');
    categories.delete('N/A');
    categories.delete('NaN');
    categories.delete('na');
    return [...categories].map(String).sort();
}
function deriveColumn(data, accessor) {
    const column = {
        Header: accessor.toString(),
        accessor,
    };
    if (data.length === 0) {
        return { type: 'text', column };
    }
    let firstIndex = 0;
    let first = data[firstIndex][accessor];
    while (first == null && firstIndex < data.length - 1) {
        firstIndex += 1;
        first = data[firstIndex][accessor];
    }
    if (first == null) {
        return { type: 'text', column };
    }
    if (typeof first === 'number') {
        return { type: 'number', column };
    }
    if (first instanceof Date) {
        return { type: 'date', column };
    }
    if (typeof first === 'boolean') {
        const acc = (row) => (row[accessor] ? String(row[accessor]) : null);
        return {
            type: 'categorical',
            column: {
                Header: column.Header,
                id: accessor.toString(),
                accessor: acc,
            },
        };
    }
    const values = new Set(String(first));
    for (let i = firstIndex + 1; i < data.length; i += 1) {
        const v = data[i][accessor];
        if (v != null) {
            values.add(String(v));
        }
    }
    if (values.size < 20 || values.size < data.length * 0.7) {
        return {
            type: 'categorical',
            column,
            options: {
                categories: cleanCategories(values),
            },
        };
    }
    return { type: 'text', column };
}
function deriveColumns(data, columns) {
    if (data.length === 0) {
        if (columns) {
            return columns.map((col) => asTextColumn(col));
        }
        return [];
    }
    return (columns !== null && columns !== void 0 ? columns : Object.keys(data[0])).map((col) => {
        const derived = deriveColumn(data, col);
        switch (derived.type) {
            case 'categorical':
                return asCategoricalColumn(derived.column, derived.options);
            case 'number':
                return asNumberColumn(derived.column, derived.options);
            case 'date':
                return asDateColumn(derived.column, derived.options);
            default:
                return asTextColumn(derived.column, derived.options);
        }
    });
}

export { BarRenderer, BarRendererFactory, BoxPlotArrayRenderer, BoxPlotArrayRendererFactory, BoxPlotRenderer, BoxPlotRendererFactory, CategoricalHistogramRenderer, CategoricalHistogramRendererFactory, CategoricalRenderer, CategoricalRendererFactory, CategoricalSetHistogramRenderer, CategoricalSetHistogramRendererFactory, CategoricalSetRenderer, CategoricalSetRendererFactory, ColorRenderer, ColorRendererFactory, DateHistogramRenderer, DateHistogramRendererFactory, DateRenderer, DateRendererFactory, DivergingBarRenderer, DivergingBarRendererFactory, GroupValueRenderer, HeatMap1DRenderer, HeatMap1DRendererFactory, IndeterminateCheckbox, NumberHistogramRenderer, NumberHistogramRendererFactory, ProportionalSymbolRenderer, ProportionalSymbolRendererFactory, StackedBarRenderer, StackedBarRendererFactory, TextRenderer, TextRendererFactory, TextSummaryRenderer, TextSummaryRendererFactory, USE_EXPAND_COLUMN_I18N_EN, USE_ROW_SELECT_COLUMN_I18N_EN, asCategoricalColumn, asCategoricalSetColumn, asColumn, asDateColumn, asDivergingNumberColumn, asNumberBoxPlotColumn, asNumberColumn, asNumbersColumn, asStackedNumberColumn, asTextColumn, categoricalFilter, categoricalGroupBy, categoricalGroupCompare, categoricalSetCompare, categoricalSetFilter, categoricalSetGroupBy, categoricalSetGroupCompare, categoricalSort, categoricalStats, cleanCategories, columnSpecificGroupByFn, computeArrayNumberStats, computeStackedValue, dateGroupBy, dateGroupByFactory, dateGroupCompare, dateGroupCompareFactory, dateStats, deriveColumn, deriveColumns, isMissing, numberGroupBy, numberGroupByFactory, numberGroupCompare, numberGroupCompareFactory, numberStats, numbersCompare, numbersCompareFactory, rangeFilter, sortCompare, sortSplitter, statsAggregate, statsAggregateArray, textGroupBy, textGroupByFactory, textGroupCompare, textStats, useGroupingOptions, useRowExpandColumn, useRowRankColumn, useRowSelectColumn, useSortingOptions, useStats };
//# sourceMappingURL=index.js.map
